import { queryClient } from "@/lib/queryClient";

/**
 * ONE CACHE ENTRY FOR USER PREFERENCES, READ FROM FOUR PLACES.
 *
 * `/api/users/me/preferences` was being fetched three times on a single page
 * load. Two components asked React Query for it under this key and were
 * correctly deduplicated; two more — UserPreferencesSync and
 * WidgetManagerContext — called `fetch` directly and so were invisible to the
 * cache.
 *
 * The wasted round trips were the smaller half of the problem. The larger half
 * is that a raw fetch is not invalidated: when UserPreferencesSync PATCHed the
 * theme, the copy AppShell was holding stayed stale until a reload, so two
 * components disagreed about the same user's settings.
 *
 * Everything that reads or writes preferences goes through this module.
 */
export const USER_PREFERENCES_KEY = ['/api/users/me/preferences'] as const;

export type UserPreferences = Record<string, string>;

/**
 * `staleTime: Infinity` because a preference only changes when this app changes
 * it, and every writer below updates the cache directly. `quiet` because a
 * failed background preference sync is not something to put a toast in front of
 * someone for — the app works without it.
 */
export const userPreferencesQuery = {
  queryKey: USER_PREFERENCES_KEY,
  staleTime: Infinity,
  retry: false,
  meta: { quiet: true },
} as const;

/**
 * Read preferences through the cache: returns the cached copy when one exists
 * and otherwise fetches once, however many callers ask at the same moment.
 * Resolves to `{}` rather than throwing, because every caller treats
 * preferences as best-effort and has a local fallback.
 */
export async function fetchUserPreferences(): Promise<UserPreferences> {
  try {
    const prefs = await queryClient.fetchQuery<UserPreferences>({ ...userPreferencesQuery });
    return prefs ?? {};
  } catch {
    return {};
  }
}

/**
 * Fold a saved preference into the cache so every reader sees it immediately,
 * without a round trip. Call this alongside the PATCH, not instead of it.
 */
export function setCachedUserPreferences(patch: UserPreferences): void {
  queryClient.setQueryData<UserPreferences>(USER_PREFERENCES_KEY, (old) => ({ ...(old ?? {}), ...patch }));
}
